import FetchError from '../FetchError'

export type UserErrorLocale = 'nb' | 'en'

/**
 * High-level classification of a thrown value. Used to decide whether the
 * server-provided detail is safe to show to the user (expected validation
 * errors) or whether a friendly generic message is required (unexpected
 * service/network failures).
 */
export type ErrorKind =
  | 'network'
  | 'auth'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'validation'
  | 'server'
  | 'unknown'

export type ClassifiedError = {
  kind: ErrorKind
  status?: number
  detail?: string
}

const STATUS_TO_KIND: Record<number, ErrorKind> = {
  400: 'validation',
  401: 'auth',
  403: 'forbidden',
  404: 'notFound',
  409: 'conflict',
  412: 'conflict',
  422: 'validation'
}

const isNetworkError = (error: unknown): error is Error =>
  error instanceof Error && error.message.toLowerCase() === 'failed to fetch'

export const classifyError = (error: unknown): ClassifiedError => {
  // Transport failures — including ones the typed API clients wrap into a
  // status-less FetchError (see useAuth's onError) — must be classified as
  // network so callers show network guidance. A status code means the server
  // answered, so that is never a pure network failure.
  if (
    isNetworkError(error) &&
    !('status' in error && typeof error.status === 'number')
  ) {
    return { kind: 'network' }
  }
  if (error instanceof FetchError) {
    const status = typeof error.status === 'number' ? error.status : undefined
    let kind: ErrorKind = 'unknown'
    if (status !== undefined) {
      kind = STATUS_TO_KIND[status] ?? (status >= 500 ? 'server' : 'unknown')
    }
    return { kind, status, detail: error.detail ?? error.message }
  }
  if (isNetworkError(error)) {
    return { kind: 'network' }
  }
  if (error instanceof Error) {
    return { kind: 'unknown', detail: error.message }
  }
  return { kind: 'unknown' }
}

/**
 * An `Error` whose message is deliberately curated user-facing copy (crafted
 * in app/business code as an expected condition, for example "Serveren
 * returnerte ingen gyldig kvittering."). `toUserMessage` shows its message
 * verbatim without classifying it — use it to opt in to verbatim display so
 * that ambiguous plain `Error`s (which may hold raw "lookup failed" text) are
 * never leaked by mistake.
 */
export class UserMessageError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UserMessageError'
  }
}

export const isUserMessageError = (error: unknown): error is UserMessageError =>
  error instanceof UserMessageError

/**
 * Server-provided text from `parseFetchError` that could leak through when the
 * backend reports it via an unexpected code or returns no problem+json body.
 * Only genuine validation messages (400/422 with a real detail) are user-facing;
 * everything else is treated as diagnostic-only.
 */
const NOT_USER_FACING = new Set([
  'fetch error',
  'unknown error',
  'internal server error',
  'bad request',
  'unprocessable entity',
  'conflict',
  'precondition failed',
  'if-match precondition failed',
  'the etag specified in the if-match header is out of date',
  'the resource has been modified since it was last retrieved'
])

export const isSafeServerDetail = (detail?: string): boolean => {
  if (detail == null) return false
  const trimmed = detail.trim()
  if (trimmed === '') return false
  return !NOT_USER_FACING.has(trimmed.toLowerCase())
}

/**
 * Substrings that strongly indicate curated Norwegian user copy. Plain
 * `Error`s in this codebase are often deliberately crafted user-facing
 * messages ("Opplastingen ble ikke bekreftet.", "Teamet er i bruk",
 * "Resultatfeil"), while raw internals tend to be English/technical
 * ("Storfe lookup failed", "lookup failed"). The markers are chosen to have
 * no meaningful collision with common English/technical wording.
 */
const NORWEGIAN_MARKERS = [
  'ikke',
  'feil',
  'bruk',
  'kunne',
  'skal',
  'finnes',
  'prøv',
  'vedvarer',
  'vennligst',
  'opplasting',
  'bekreftet',
  'gyldig',
  'kvittering',
  'forespørselen',
  'innlogging',
  'slett',
  'lagre',
  'kontroller'
]

export const looksUserFacing = (message?: string): boolean => {
  if (message == null) return false
  const lower = message.toLowerCase()
  return NORWEGIAN_MARKERS.some((marker) => lower.includes(marker))
}

type ErrorKindCopy = { headline: string; guide?: string }

const COPY: Record<UserErrorLocale, Record<ErrorKind, ErrorKindCopy>> = {
  nb: {
    network: {
      headline: 'Vi klarte ikke å kontakte serveren.',
      guide: 'Sjekk nettverkstilkoblingen og prøv igjen.'
    },
    auth: {
      headline: 'Du er ikke lenger innlogget.',
      guide: 'Logg inn på nytt og prøv igjen.'
    },
    forbidden: {
      headline: 'Du har ikke tilgang til denne handlingen.',
      guide: 'Kontakt brukerstøtte hvis du mener dette er feil.'
    },
    notFound: {
      headline: 'Innholdet kunne ikke finnes.',
      guide: 'Det kan ha blitt slettet eller flyttet.'
    },
    conflict: {
      headline: 'Handlingen kunne ikke fullføres.',
      guide:
        'Handlingen kan ikke utføres med opplysningene som vises nå. Ta vare på eller kopier ulagrede endringer før du laster siden på nytt for å se oppdaterte opplysninger.'
    },
    validation: {
      headline: 'Forespørselen ble ikke godkjent.',
      guide: 'Kontroller opplysningene og prøv igjen.'
    },
    server: {
      headline: 'Det oppstod en midlertidig feil.',
      guide: 'Prøv igjen. Vedvarer problemet, kontakt brukerstøtte.'
    },
    unknown: {
      headline: 'Noe gikk galt.',
      guide: 'Prøv igjen. Vedvarer problemet, kontakt brukerstøtte.'
    }
  },
  en: {
    network: {
      headline: "We couldn't reach the server.",
      guide: 'Check your network connection and try again.'
    },
    auth: {
      headline: 'You are no longer signed in.',
      guide: 'Sign in again and try again.'
    },
    forbidden: {
      headline: 'You do not have access to this action.',
      guide: 'Contact support if you believe this is a mistake.'
    },
    notFound: {
      headline: 'The content could not be found.',
      guide: 'It may have been deleted or moved.'
    },
    conflict: {
      headline: 'The action could not be completed.',
      guide:
        'This action cannot be completed with the information currently shown. Keep or copy any unsaved changes before reloading the page to see up-to-date information.'
    },
    validation: {
      headline: 'The request was not accepted.',
      guide: 'Check the entered data and try again.'
    },
    server: {
      headline: 'A temporary error occurred.',
      guide: 'Try again. If the problem persists, contact support.'
    },
    unknown: {
      headline: 'Something went wrong.',
      guide: 'Try again. If the problem persists, contact support.'
    }
  }
}

/**
 * Produces a user-facing Norwegian/English message for a thrown value.
 *
 * - `UserMessageError` (crafted, already-curated user copy such as "Serveren
 *   returnerte ingen gyldig kvittering.") is shown verbatim — app code opts in
 *   deliberately.
 * - Plain `Error`s whose message reads as curated Norwegian copy (e.g.
 *   "Opplastingen ble ikke bekreftet.", "Teamet er i bruk") are also shown
 *   verbatim, because this codebase deliberately authors such messages.
 * - Expected validation errors (400/422 with a real server detail) show that
 *   detail prefixed by the action, because it is the user's actionable
 *   feedback.
 * - Unexpected service/network/conflict failures and ambiguous raw-English
 *   plain `Error`s (such as "lookup failed") get friendly copy with a
 *   try-again/support pointer. The raw detail is intentionally not shown
 *   there; retain it separately for logging (`useErrorToast` logs it).
 *
 * @param error    The thrown value.
 * @param action   Norwegian infinitive nominal phrase, e.g. `'lagre hensikten'`
 *                 → `'Kunne ikke lagre hensikten.'`. Optional; without it only
 *                 the generic kind copy is used.
 * @param opts.locale Display language; defaults to `'nb'`.
 */
export const toUserMessage = (
  error: unknown,
  action?: string,
  opts: { locale?: UserErrorLocale; fallback?: string } = {}
): string => {
  if (isUserMessageError(error)) return error.message

  const { locale = 'nb' } = opts
  const classified = classifyError(error)
  const copy = COPY[locale][classified.kind]

  const prefix =
    locale === 'nb'
      ? action
        ? `Kunne ikke ${action}. `
        : `${copy.headline} `
      : action
        ? `Could not ${action}. `
        : `${copy.headline} `

  // Plain `Error`s (no status) whose message reads as curated Norwegian user
  // copy are shown verbatim — the codebase deliberately authors messages such
  // as "Opplastingen ble ikke bekreftet." and "Teamet er i bruk". Ambiguous
  // raw-English plain errors fall through to friendly copy below.
  if (classified.kind === 'unknown' && looksUserFacing(classified.detail)) {
    return classified.detail as string
  }

  const suffix =
    classified.status === 412
      ? locale === 'nb'
        ? 'Noe ble endret av noen andre samtidig. Ta vare på eller kopier ulagrede endringer før du laster siden på nytt for å hente siste versjon.'
        : 'Something was changed by someone else at the same time. Keep or copy any unsaved changes before reloading the page to get the latest version.'
      : classified.kind === 'validation' &&
          isSafeServerDetail(classified.detail)
        ? classified.detail
        : (copy.guide ?? copy.headline)

  return `${prefix}${suffix}`
}

export const toSaveErrorMessage = (
  error: unknown,
  action?: string,
  opts: {
    locale?: UserErrorLocale
    conflictMessage?: string
  } = {}
): string => {
  const { locale = 'nb', conflictMessage } = opts
  const { status, kind } = classifyError(error)
  if (status === 409 && conflictMessage) return conflictMessage
  if (isUserMessageError(error)) return error.message
  // Plain errors can contain diagnostics, even when they include Norwegian words.
  return toUserMessage(kind === 'unknown' ? undefined : error, action, {
    locale
  })
}
